import React, { useEffect, useState, useCallback } from "react";
import WSBackButton from "../../../Components/BackButton/BackButton";
import {
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import http from "../../../commonFunctions/httpClient";
import { updateMainHeader } from "../../../commonFunctions/CommonFunctions";
import WSSelectField from "../../../Components/Select/WSSelect";
import { WSIcons } from "../../../commonFunctions/iIcons";
import { LabInvoiceReport } from "./LabInvoiceReport";

interface LabService {
  id: string;
  bookingId: string;
  labServiceId: string;
  labService: {
    id: string;
    name: string;
    alsoKnownAs: string;
    mrpPrice: number;
    finalPrice: number;
  };
  rate: number;
  reportUrl: string;
  isReportGenerated: boolean;
}

interface LabBooking {
  id: string;
  patients: string;
  labId: string;
  userId: string;
  collectionType: string;
  totalAmount: number;
  orderNumber: string;
  paymentMode: string;
  paymentStatus: string;
  status: string;
  collectionFee: number;
  serviceFee: number;
  orderdate: string;
  labservice: LabService[];
}

interface PaymentDetails {
  paymentId: string;
  labBookingId: string;
  labBooking: LabBooking;
  invoiceUrl: string;
  amountPaid: number;
  paymentMethod: string;
  transactionId: string;
  status: string;
  paymentType: string;
  invoiceNumber: string;
  paymentDate: string;
  createdDate: string;
}

export const RevenuePaymentDetails: React.FC<any> = React.memo((props) => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const params = useParams<{ id: string }>();

  const id = params?.id;

  const { control, register, handleSubmit, setValue } = useForm();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paymentData, setPaymentData] = useState<PaymentDetails | null>(null);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);

  useEffect(() => {
    updateMainHeader("Payment Details");
    breadCrumbsStore.updateBreadCrumbs([
      { "Revenue & Payments": "revenue-payments" },
      { "Payment Details": `/revenue-details/${bookingId}` },
    ]);
  }, [bookingId]);

  const fetchPaymentDetails = useCallback(async () => {
    if (!bookingId) {
      setError("Booking ID is required");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");
      const response: any = await http.get(
        `lab/LabPayment/booking/${bookingId}`
      );
      console.log("idbook", bookingId);

      const data = response.data || response || {};
      setPaymentData(data);

      if (data.status) {
        setValue("refund", data.status);
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch payment details");
      setPaymentData(null);
    } finally {
      setLoading(false);
    }
  }, [bookingId, setValue]);

  useEffect(() => {
    fetchPaymentDetails();
  }, [fetchPaymentDetails]);

  const onSubmit = (data: any) => {
    console.log("Form submitted:", data);
  };
  const handleOpenInvoice = () => {
    setInvoiceModalOpen(true);
  };

  if (loading) {
    return (
      <div style={{ padding: 20, textAlign: "center" }}>
        Loading payment details...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 20, color: "red" }}>
        {error}
        <button onClick={fetchPaymentDetails} style={{ marginLeft: 10 }}>
          Retry
        </button>
      </div>
    );
  }

  if (!paymentData) {
    return (
      <div style={{ padding: 20 }}>
        No payment details found for this booking.
      </div>
    );
  }

  const booking = paymentData.labBooking;
  const firstLabServiceItem = booking?.labservice?.[0];

  return (
    <>
      <WSBackButton id="btnScheduleBack" url="/revenue-payments" />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Paper
          elevation={0}
          sx={{ p: 3, mt: 2, borderRadius: "8px", backgroundColor: "#FFFFFF" }}
        >
          <Typography sx={{ fontSize: "1rem", fontWeight: "bold", mb: 2 }}>
            Quick Info
          </Typography>
          <Grid sx={{ border: "1px solid #828584", borderRadius: "8px", p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="GrayText">
                  Payment ID
                </Typography>
              </Grid>
              <Grid item xs={12} sm={7}>
                <Typography variant="body2" color="GrayText">
                  : {paymentData.paymentId || "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="GrayText">
                  Date & Time
                </Typography>
              </Grid>
              <Grid item xs={12} sm={7}>
                <Typography variant="body2" color="GrayText">
                  :{" "}
                  {paymentData.paymentDate
                    ? new Date(paymentData.paymentDate).toLocaleString()
                    : ""}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="GrayText">
                  Status
                </Typography>
              </Grid>
              <Grid item xs={12} sm={7}>
                <Typography variant="body2" color="GrayText">
                  : {paymentData.status || ""}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mt: 2,
                borderRadius: "8px",
                backgroundColor: "#FFFFFF",
              }}
            >
              <Typography sx={{ fontSize: "1rem", fontWeight: "bold", mb: 2 }}>
                Patient & Order Info
              </Typography>
              <Grid
                sx={{ border: "1px solid #828584", borderRadius: "8px", p: 2 }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" color="GrayText">
                      Patient Name
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <Typography variant="body2" color="GrayText">
                      : {booking?.patients || " "}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" color="GrayText">
                      Category
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <Typography variant="body2" color="GrayText">
                      : {firstLabServiceItem?.labService?.name || ""}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" color="GrayText">
                      Sample Collected
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <Typography variant="body2" color="GrayText">
                      : {booking?.collectionType || ""}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" color="GrayText">
                      Booking Status
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <Typography variant="body2" color="GrayText">
                      : {booking?.status || ""}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mt: 2,
                borderRadius: "8px",
                backgroundColor: "#FFFFFF",
              }}
            >
              <Typography sx={{ fontSize: "1rem", fontWeight: "bold", mb: 2 }}>
                Payment Details
              </Typography>
              <Grid
                sx={{ border: "1px solid #828584", borderRadius: "8px", p: 2 }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" color="GrayText">
                      Amount Paid
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <Typography variant="body2" color="GrayText">
                      : ₹{paymentData.amountPaid?.toLocaleString() || ""}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" color="GrayText">
                      Test Rate
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <Typography variant="body2" color="GrayText">
                      : ₹{firstLabServiceItem?.rate?.toLocaleString() || ""}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" color="GrayText">
                      Home Collection
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <Typography variant="body2" color="GrayText">
                      : ₹{booking?.collectionFee?.toLocaleString() || ""}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" color="GrayText">
                      Transaction ID
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <Typography variant="body2" color="GrayText">
                      : {paymentData.transactionId || ""}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" color="GrayText">
                      Invoice Number
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <Typography variant="body2" color="GrayText">
                      : {paymentData.invoiceNumber || ""}
                    </Typography>
                  </Grid>

                  {/* ✅ REPORT STATUS WITH CONDITIONAL BUTTONS */}
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" color="GrayText">
                      Report Status
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    :{" "}
                    <Link
                      style={{ color: "#098963" }}
                      onClick={handleOpenInvoice}
                    >
                      View Invoice
                    </Link>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mt: 2,
                borderRadius: "8px",
                backgroundColor: "#FFFFFF",
              }}
            >
              <Typography sx={{ fontSize: "1rem", fontWeight: "bold", mb: 2 }}>
                Actions
              </Typography>
              <Grid
                sx={{ border: "1px solid #828584", borderRadius: "8px", p: 2 }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" color="GrayText">
                      Refund Status:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <WSSelectField
                      rules={{ required: true }}
                      {...register("refund")}
                      id="refund"
                      control={control}
                      label=""
                      name="refund"
                      defaultValue={paymentData.status || ""}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
        <Dialog
          open={invoiceModalOpen}
          onClose={() => setInvoiceModalOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { height: "90vh" } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Lab Invoice</Typography>
            <IconButton onClick={() => setInvoiceModalOpen(false)}>
              <WSIcons.CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: "100%", overflow: "auto" }}>
            {paymentData ? (
              <LabInvoiceReport bookingId={bookingId!} />
            ) : (
              <Box p={4} textAlign="center">
                <Typography>Loading invoice...</Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </form>
    </>
  );
});

export default RevenuePaymentDetails;
